// @ts-check

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  Page,
  Card,
  BlockStack,
  Text,
  Grid,
} from "@shopify/polaris";
import { keepPreviousData } from "@tanstack/react-query";

import { UtmSetOrdersTable } from "components/UtmSetOrders/UtmSetOrdersTable";
import { FetchOrdersControls } from "components/FetchOrderControls/FetchOrdersControls";
import { StatBox } from "components/StatBox/StatBox";

import { useGlobalContext } from "context/GlobalContext";
import { useUtmSetContext } from "context/UtmSetContext";

import { useAppQuery } from "hooks/use-app-query";
import { useSearchParam } from "hooks/use-search-param";
import { useFormatCurrency } from "hooks/use-format-currency";

export default function ViewUTMSet() {
  const navigate = useNavigate();
  const { refetchUtmSetOrders, isFetchingUtmSetOrders } = useUtmSetContext();
  const { dates } = useGlobalContext();
  const utmSource = useSearchParam("utm_source");
  const utmMedium = useSearchParam("utm_medium");
  const utmCampaign = useSearchParam("utm_campaign");
  const utmContent = useSearchParam("utm_content");
  const utmTerm = useSearchParam("utm_term");
  const formatCurrency = useFormatCurrency();

  const utmSetsMetricsUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }


    if (utmSource) {
      params.utm_source = utmSource;
    }
    if (utmMedium) {
      params.utm_medium = utmMedium;
    }
    if (utmCampaign) {
      params.utm_campaign = utmCampaign;
    }
    if (utmContent) {
      params.utm_content = utmContent;
    }
    if (utmTerm) {
      params.utm_term = utmTerm;
    }

    return `/api/utm-sets/metrics?${new URLSearchParams(params).toString()}`;
  }, [
    dates,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
  ]);

  const {
    data: utmSetMetricsResponse,
    refetch: refetchUtmSetMetrics,
    isPlaceholderData: isPlaceholderUtmSetMetrics,
    isLoading: isLoadingUtmSetMetrics,
    isRefetching: isRefetchingUtmSetMetrics,
    isFetching: isFetchingUtmSetMetrics,
  } = useAppQuery({
    url: utmSetsMetricsUrl,
    extraQueryKeys: ["utmSets", "utmSetMetrics"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const utmSetMetrics = utmSetMetricsResponse?.utmSetMetrics;

  const isInitialLoadingAdCampaignStats = isLoadingUtmSetMetrics || (isFetchingUtmSetMetrics && !utmSetMetricsResponse);

  const handleRefresh = () => {
    refetchUtmSetOrders();
    refetchUtmSetMetrics();
  };

  const allParams = [
    {
      title: "UTM Source",
      value: utmSource,
    },
    {
      title: "UTM Medium",
      value: utmMedium,
    },
    {
      title: "UTM Campaign",
      value: utmCampaign,
    },
    {
      title: "UTM Content",
      value: utmContent,
    },
    {
      title: "UTM Term",
      value: utmTerm,
    },
  ];

  return (
    <Page
      title="View UTM set"
      backAction={{
        content: "Back to UTM sets",
        onAction: () => {
          navigate("/utm-sets");
        },
      }}
      fullWidth
    >
      <BlockStack gap="400">
        <FetchOrdersControls
          loading={isFetchingUtmSetOrders}
          onRefresh={handleRefresh}
        />

        <BlockStack gap="300">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: "1rem" }}>
            {allParams.map((utmParam, index) => {
              return (
                <Card key={utmParam.title}>
                  <BlockStack gap="200">
                    <Text variant="headingXs" as="h3">{utmParam.title}</Text>
                    {utmParam.value || "-"}
                  </BlockStack>
                </Card>
              );
            })}
          </div>

          <Grid>
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
              <StatBox
                title="Revenue"
                value={utmSetMetrics?.total_revenue ? formatCurrency(utmSetMetrics.total_revenue) : formatCurrency(0)}
                loading={isInitialLoadingAdCampaignStats}
              />
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
              <StatBox
                title="Sales"
                value={utmSetMetrics?.total_sales || 0}
                loading={isInitialLoadingAdCampaignStats}
              />
            </Grid.Cell>
            <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 2, lg: 4, xl: 4 }}>
              <StatBox
                title="AOV"
                value={utmSetMetrics?.average_order_value ? formatCurrency(utmSetMetrics.average_order_value) : formatCurrency(0)}
                loading={isInitialLoadingAdCampaignStats}
              />
            </Grid.Cell>
          </Grid>

          <Layout>
            <Layout.Section>
              <Card padding="0">
                <UtmSetOrdersTable />
              </Card>
            </Layout.Section>
          </Layout>
        </BlockStack>
      </BlockStack>
    </Page>
  );
};
